import { ColumnDef, ColumnDefByColName } from '../../../types'

// Ref: node_modules/element-plus/es/components/cascader-panel/src/node.d.ts
export type NestedColumnSelectionCascaderNode = {
  level: number
  data: NestedColumnSelectionSelectionItem
} & any

export type NestedColumnSelectionSelectionItem = {
  value: string
  label: string
  leaf: boolean
  metaData?: {
    isRelationalColumn?: boolean
    colDef?: ColumnDef
  }
}
export type NestedColumnSelectionLazyloadResolveFunction = (
  selectionItems: NestedColumnSelectionSelectionItem[],
) => void
export type NestedColumnSelectionLazyloadFunction = (
  node: NestedColumnSelectionCascaderNode,
  resolve: NestedColumnSelectionLazyloadResolveFunction,
) => void

/**
 * ネストされたカラム選択肢 を filtering したい場合に 使用する
 */
export type NestedColumnSelectionSelectionsConvertFunction = (
  items: NestedColumnSelectionSelectionItem[],
  currentNode: NestedColumnSelectionCascaderNode,
) => Promise<NestedColumnSelectionSelectionItem[]>

/**
 * ネストされたカラム選択肢のための 遅延ロード関数を生成する関数
 *
 * ## Usecases
 * - レコードの検索 (nest 許可) 時
 * - モデルの 特定のカラムを選択させたいとき
 *
 * ## Args:
 * - スタートポイントとなる Columns (必須)
 * - maxNestedLevel (default: 10)
 * - selectionsConvertFunction (カラム選択肢の filter or 追加などを実施できる 関数)
 */
export const createNestedColumnSelectionLazyloadFunction = (
  columns: ColumnDefByColName,
  {
    maxNestedLevel = null,
    selectionsConvertFunction = null,
  }: {
    maxNestedLevel?: number
    selectionsConvertFunction?: NestedColumnSelectionSelectionsConvertFunction
  } = {},
): NestedColumnSelectionLazyloadFunction => {
  if (maxNestedLevel === null) {
    maxNestedLevel = $core.$configVars.get('maxRelationalFilterNestedLevel', 9)
  }
  const itemsResolver = async (
    items: NestedColumnSelectionSelectionItem[],
    currentNode: NestedColumnSelectionCascaderNode,
    resolve: NestedColumnSelectionLazyloadResolveFunction,
  ) => {
    if (selectionsConvertFunction) {
      items = await selectionsConvertFunction(items, currentNode)
    }
    resolve(items)
  }
  return (
    node: NestedColumnSelectionCascaderNode,
    resolve: NestedColumnSelectionLazyloadResolveFunction,
  ) => {
    const level = node.level
    const isAlreadyMaxLevel = level >= maxNestedLevel
    /**
     * ## Logic
     * - ここでの node は 下記2つの 状態を持つ
     *   - 初期状態 で 開いたとき (node.level === 0)
     *   - ネストされた状態 (node.level > 0)
     */
    if (level === 0) {
      const items = Object.values(columns).reduce((acc, colDef) => {
        const item = colDefToCascaderNode(colDef.name, colDef, isAlreadyMaxLevel)
        if (item) {
          acc.push(item)
        }
        return acc
      }, [])
      itemsResolver(items, node, resolve)
    } else {
      // ネストされた状態の場合は、親のカラムの子カラムを取得する
      const selectedColDef: ColumnDef = node.data.metaData.colDef
      const targetModelName =
        selectedColDef.type === 'RELATIONSHIP_MANY_TO_ONE'
          ? selectedColDef.relationshipManyToOne?.collectionName
          : selectedColDef.relationshipOneToMany?.collectionName
      if (targetModelName) {
        const relationModel = $core.$models[targetModelName]
        const childItems = Object.values(relationModel.columns).reduce((acc, colDef) => {
          const item = colDefToCascaderNode(colDef.name, colDef, isAlreadyMaxLevel)
          if (item) {
            acc.push(item)
          }
          return acc
        }, [])
        itemsResolver(childItems, node, resolve)
      }
    }
  }
}

const nestableColumnTypes = ['RELATIONSHIP_MANY_TO_ONE', 'RELATIONSHIP_ONE_TO_MANY']

const colDefToCascaderNode = (
  colName: string,
  colDef: ColumnDef,
  isAlreadyMaxLevel = false,
): NestedColumnSelectionSelectionItem | null => {
  const isRelationalColumn = nestableColumnTypes.indexOf(colDef.type) >= 0
  // すでに maxLevel であるなら reation カラムは 除外
  if (isAlreadyMaxLevel && isRelationalColumn) {
    return null
  }
  return {
    value: colName,
    label: colDef.label || colName,
    // true であれば 子カラムがないことを示す
    leaf: isAlreadyMaxLevel ? true : !isRelationalColumn,
    metaData: {
      colDef,
      isRelationalColumn,
    },
  }
}
